import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	TemplateRef,
	ViewChild
} from "@angular/core";

import {GridComponent, GridDataResult, PageChangeEvent, SelectionEvent} from '@progress/kendo-angular-grid';

import {LogCat, LogManager} from "../../../log";

import {VerticalListScrollableItemDirective} from "./vertical.list.scrollable.item.directive";

/*
* Selector: cp-vertical-list-scrollable
* Change Detection: OnPush
* View Encapsulation: Emulated
*
* Inputs:
*	items: [] 				- mandatory - array of list items
*	field: string 			- mandatory - displayed field
*	rowHeight: number		- optional - height of item row
*	pageSize: number		- optional - number of rows to render
*	gotoRowNumber: number	- optional - scroll to row number
*
* Outputs:
* 	itemClick: EventEmitter<any> 		- optional - emits item click
* 	itemDoubleClick: EventEmitter<any>	- optional - emits item double-click
* */

@Component({
	selector: 'cp-vertical-list-scrollable',
	templateUrl: './vertical.list.scrollable.html',
	styleUrls: ['./vertical.list.scrollable.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerticalListScrollableComponent implements OnChanges, AfterViewInit, OnDestroy {

	@Input() items: Array<any>;
	@Input() field: string;
	@Input() rowHeight: number = 35;
	@Input() pageSize: number = 60;
	@Input() gotoRowNumber: number = 0;
	@Input() maxVisibleRows: number;
	@Input() navigatable: boolean = true;
	@Input() selectable:boolean;

	@Output() itemClick: EventEmitter<any> = new EventEmitter();
	@Output() itemDoubleClick: EventEmitter<any> = new EventEmitter();
	@Output() itemSelected: EventEmitter<any> = new EventEmitter();
	@Output() dataSelected: EventEmitter<any> = new EventEmitter();
	@Output() rightClick: EventEmitter<any> = new EventEmitter();

	@ContentChild(VerticalListScrollableItemDirective, {static:false,read: TemplateRef}) itemTemplate: TemplateRef<any>;

	@ViewChild('grid',{static:false}) grid: GridComponent;
	@ViewChild('grid',{static:true,read:ElementRef}) anchor: ElementRef;


	public gridView: GridDataResult;
	public skip: number = 0;
	public view = new Array(this.pageSize).fill({}).map(x => ({}));
	public selectedItem:any;

	constructor() {
		LogManager.debug(LogCat.UI.default,"VerticalListScrollableComponent.constructor");
	}

	public get actualHeight(){
		return this.maxVisibleRows ? (Math.min(this.maxVisibleRows, this.view.length) * this.rowHeight) + 3 : '100%';
	}

	public focus() {
		if (this.grid) {
			this.grid.focusCell(1, 0);
		}
	}

	public onKeyUp(e) {
		this.itemSelected.emit(e.target.textContent);
		this.dataSelected.emit(this.grid.activeRow.dataItem.originalData);
	}

	public selectionChange($event:SelectionEvent){
		this.selectedItem=$event.selectedRows[0]?.dataItem;
		this.dataSelected.emit(this.selectedItem);
	}

	public ngOnChanges(changes: SimpleChanges) {
		LogManager.debug(LogCat.UI.default,"VerticalListScrollableComponent.ngOnChanges");

		// if (changes.items && !changes.gotoRowNumber) {
		// 	console.log("VerticalListScrollableComponent.ngOnChanges -> skip = 0");
		// 	this.skip = 0;
		// }

		if (changes.gotoRowNumber) {
			this.skip = this.gotoRowNumber;
		}

		this.loadItems();
	}

	/* ***************************************************************************
	* Public Methods
	* ************************************************************************** */

	/*
	* Select Item Handler
	* */
	public defaultItemClickHandler(item: any) {
		LogManager.debug(LogCat.UI.default,"VerticalListScrollableComponent.defaultItemClickHandler");
		this.itemClick.emit(item);
	}

	/*
	* Un-Select Item Handler
	* */
	public defaultItemDoubleClickHandler(item: any) {
		LogManager.debug(LogCat.UI.default,"VerticalListScrollableComponent.defaultItemDoubleClickHandler");
		this.itemDoubleClick.emit(item);
	}

	/*
	* Page Change Handler
	* */
	public pageChange(event: PageChangeEvent): void {
		LogManager.debug(LogCat.UI.default,"VerticalListScrollableComponent.pageChange");
		this.skip = event.skip;
		this.loadItems();
	}

	public ngAfterViewInit(): void {
		this.anchor.nativeElement.addEventListener('contextmenu', this.onRightClick);
	}
	public onRightClick= (target:PointerEvent)=>{
		let currEl: HTMLElement = <any>target.target;
		let lastTr: HTMLElement;

		while (currEl !== this.anchor.nativeElement) {
			if (currEl.tagName === "TR") {
				lastTr = currEl;
			}
			currEl = currEl.parentElement;
		}

		let rowIdx = Array.from((<HTMLElement>lastTr.parentNode).children).indexOf(lastTr);
		this.rightClick.emit(this.items[rowIdx]);
	};

	public ngOnDestroy() {
		this.anchor.nativeElement.removeEventListener('contextmenu', this.onRightClick);
	}

	/* ***************************************************************************
	* Private Methods
	* ************************************************************************** */

	/*
	* Load Page Items
	* */
	private loadItems(): void {
		LogManager.debug(LogCat.UI.default,"VerticalListScrollableComponent.loadItems");

		const currentView = this.items.slice(this.skip, this.skip + this.pageSize);

		const removeCount = this.view.length - currentView.length;

		if (removeCount > 0) {
			this.view.splice(currentView.length - 1, removeCount);
		}

		currentView.forEach((item, index) => {
			if (!this.view[index]) {
				this.view[index] = {};
			} else {
				Object.keys(this.view[index]).forEach(key => {
					this.view[index][key] = undefined;
				});
			}
			Object.assign(this.view[index], item, {originalData: item});
		});

		this.gridView = {
			data: this.view,
			total: this.items.length
		};
	}
}
